﻿import React, { useState, useMemo, useContext } from 'react';
import Context, { context_type } from '@Util/Context';
import { useTranslation } from 'react-i18next';
import { str_isEmpty } from '@Util/Utils';
import * as Api from '@Util/Api';
import { apiBase } from '@Util/GlobalValues';
import dayjs from 'dayjs';

import Table_Head from '@Elements/Table/Table_Head';
import Table_Data from '@Elements/Table/Table_Data';
import Edit_Projects_Popup from '@Modules/Popups/Edit_Projects_Popup';
import {
    Icon_Unchecked_Circle,
    Icon_Checked_Circle,
    Icon_Recycle,
    Icon_Trash
} from '@Elements/ImageElements/Icons';

import {
    Reminders_Table_Props,
    Reminder
} from '@Util/Types';

export default function Reminders_Table(props: Reminders_Table_Props): JSX.Element {
    const [t] = useTranslation();
    const {
        set_status_update,
        fetch_reminders
    }: context_type = useContext(Context);

    const [sort_direction, set_sort_direction] = useState<"Ascending" | "Descending">("Ascending");
    const [active_sorting_column, set_active_sorting_column] = useState<string>("createdDate");

    const table_data: Reminder[] = useMemo(() => {
        if (!props.data || props.data.length === 0) {
            return [];
        }

        // Create a shallow copy of the data to avoid mutating the original array
        const filtered_data: Reminder[] = [...props.data];

        // Perform sorting
        return filtered_data.sort((a: Reminder, b: Reminder) => {
            if (sort_direction === "Ascending") {
                return a[active_sorting_column] > b[active_sorting_column] ? 1 : -1;
            } else {
                return a[active_sorting_column] < b[active_sorting_column] ? 1 : -1;
            }
        });
    }, [props.data, sort_direction, active_sorting_column]);

    function handleSetSortingOnTableHead(col: string): void {
        if (str_isEmpty(col)) {
            throw new Error("Handle sorting on table: Column is empty");
        }

        if (col === active_sorting_column) {
            if (sort_direction == "Ascending") {
                return set_sort_direction("Descending");
            } else {
                return set_sort_direction("Ascending");
            }
        }

        set_active_sorting_column(col);
        set_sort_direction("Ascending");
    }

    function Empty_Table(): JSX.Element {
        return (
            <tr>
                <Table_Data align="left">
                    <></>
                </Table_Data>
                <Table_Data align="left">
                    <span className="Table_Data_Reminder_Content">{t('Text.NoProjectsToShow')}</span>
                </Table_Data>
                <Table_Data align="right">
                    <></>
                </Table_Data>
            </tr>
        )
    }

    async function set_reminder_as_completed(reminder: Reminder): Promise<void> {
        try {
            const api_call = await Api.callApi(`${apiBase}/CompleteReminder`, {
                reminderId: reminder.id
            });

            if (api_call?.status === "Invalid" || api_call?.status === "Error") {
                set_status_update({
                    visible: true,
                    text: api_call?.details,
                    type: "Error"
                });
            } else {
                set_status_update({
                    visible: true,
                    text: t('Text.ApiSaved'),
                    type: "Success"
                });

                fetch_reminders();
            }
        } catch (err) {
            set_status_update({
                visible: true,
                text: err.message || t('Error.UnknownError'),
                type: "Error"
            });
        }
    }

    return (
        <>
            <div className="Table_Wrapper">
                <table className="Table">
                    <thead>
                        <tr>
                            <Table_Head
                                align="left"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text=""
                                sortingAction={() => handleSetSortingOnTableHead('dueDate')}
                                sortingDataName="dueDate"
                                style={{ width: "5%" }}

                            />
                            <Table_Head
                                align="left"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.Content')}
                                sortingAction={() => handleSetSortingOnTableHead('content')}
                                sortingDataName="content"
                                style={{ width: "70%" }}

                            />
                            <Table_Head
                                align="left"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.DueDate')}
                                sortingAction={() => handleSetSortingOnTableHead('dueDate')}
                                sortingDataName="dueDate"
                                style={{ width: "20%" }}

                            />
                            <Table_Head
                                align="right"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text=""
                                sortingAction={() => handleSetSortingOnTableHead('delete')}
                                sortingDataName="delete"
                                style={{ width: "5%" }}

                            />
                        </tr>
                    </thead>
                    <tbody>
                        {table_data && table_data.length > 0 ? (
                            table_data.map((reminder: Reminder, index: number) => (
                                <Reminder_Table_Row
                                    key={reminder.id || index}
                                    reminder={reminder}
                                    index={index}
                                    set_reminder_as_completed={set_reminder_as_completed}
                                />
                            ))
                        ) : (
                            <Empty_Table />
                        )}
                    </tbody>
                </table>
            </div>
            {/* INSERT EDIT REMINDER POPUP HERE */}
        </>
    );
}

function Reminder_Table_Row(props: { reminder: Reminder, index: number, set_reminder_as_completed: (reminder: Reminder) => Promise<void> }): JSX.Element {
    const {
        set_status_update,
        fetch_reminders,
        set_prompt_information,
        logged_in_user
    }: context_type = useContext(Context);
    const [t] = useTranslation();
    const [check_icon_is_hovered, set_check_icon_is_hovered] = useState<boolean>(false);

    if (!props.reminder)
        return;

    async function delete_reminder(): Promise<void> {
        try {
            const api_call = await Api.callApi(`${apiBase}/DeleteRecurringReminder`, {
                reminderId: props?.reminder?.id
            });

            if (api_call?.status === "Invalid" || api_call?.status === "Error") {
                set_status_update({
                    visible: true,
                    text: api_call?.details,
                    type: "Error"
                });
            } else {
                set_status_update({
                    visible: true,
                    text: t('Text.ApiSaved'),
                    type: "Success"
                });

                fetch_reminders();
            }
        } catch (err) {
            set_status_update({
                visible: true,
                text: err.message || t('Error.UnknownError'),
                type: "Error"
            });
        } finally {
            set_prompt_information({
                open: false,
                prompt_headline: "",
                prompt_text: "",
                on_accept_action: null,
                on_cancel_action: null
            });
        }
    }

    return (
        <tr key={`reminders_table_row${props.index}`}>
            <Table_Data
                align="left"
                className="Table_Data_With_Buttons"
                style={{ width: "5%" }}
            >
                <button
                    type="button"
                    className="Button_CheckMark"
                    onClick={(e) => {
                        e.stopPropagation();
                        props.set_reminder_as_completed(props.reminder);
                    }}
                    onMouseEnter={() => set_check_icon_is_hovered(true)}
                    onMouseLeave={() => set_check_icon_is_hovered(false)}
                    onTouchStart={() => set_check_icon_is_hovered(true)}
                    onTouchEnd={() => set_check_icon_is_hovered(false)}
                    onFocus={() => set_check_icon_is_hovered(true)}
                    onBlur={() => set_check_icon_is_hovered(false)}
                    aria-label={t('Text.SetReminderAsCompleted')}
                >
                    {check_icon_is_hovered ? <Icon_Checked_Circle /> : <Icon_Unchecked_Circle />}
                </button>
            </Table_Data>
            <Table_Data
                align="left"
                className="Table_Data_With_Buttons"
                style={{ width: "75%" }}
            >
                <span
                    className={dayjs(props.reminder.dueDate) < dayjs() ? 'Overdue' : ''}
                    title={props?.reminder?.recurring !== "None" ? t('Text.RecurringReminder') : ""}
                >
                    {
                        props?.reminder?.recurring !== "None" &&
                        <Icon_Recycle />
                    }
                    {props.reminder.content}
                </span>
            </Table_Data>
            <Table_Data
                align="left"
                className="Table_Data_With_Buttons"
                style={{ width: "20%" }}
            >
                {dayjs(props.reminder.dueDate).format("YYYY-MM-DD HH:mm")}
            </Table_Data>
            <Table_Data
                align="right"
                className="Table_Data_With_Buttons"
                style={{ width: "5%" }}
            >
                {
                    props?.reminder?.recurring !== "None" && props?.reminder?.createdBy === logged_in_user?.id.toString() &&
                    <button
                        type="button"
                        className="Table_Button Table_Button--Delete"
                        onClick={() => set_prompt_information({
                            open: true,
                            prompt_headline: t('Text.DeleteObject'),
                            prompt_text: t('Text.AreYouSureDeleteObject'),
                            on_accept_action: () => delete_reminder(),
                            on_cancel_action: () => set_prompt_information({
                                open: false,
                                prompt_headline: "",
                                prompt_text: "",
                                on_accept_action: null,
                                on_cancel_action: null
                            })
                        })}
                    >
                        <Icon_Trash />
                    </button>
                }
            </Table_Data>
        </tr>
    )
}